/**
 * Gestión de almacén: almacenes, artículos y movimientos manuales.
 *
 * El módulo de inventario tenía nueve operaciones en la API y ninguna forma
 * de invocarlas. Y hay un orden que no es opcional: sin almacenes y sin
 * artículos no se puede registrar ni un solo movimiento, así que un sistema
 * recién instalado no podía dar entrada a nada.
 *
 * De las nueve, aquí están las cinco que se hacen a mano desde la oficina.
 * Las otras —salidas por consumo, reservas y liberaciones— las genera el
 * trabajo de campo: el técnico marca material usado y el sistema deduce la
 * salida (principio P4: el dato se captura una vez).
 */

import { useCallback, useEffect, useState } from 'react';
import { useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';
import { DetailShell, type Tab } from './detail/detail-shell.js';
import { useForm } from '../forms/use-form.js';
import { CheckField, FormSection, SelectField, TextField } from '../forms/fields.js';
import { LabelPicker } from './forms/label-picker.js';
import { required, type FieldRules } from '../forms/form-state.js';

interface Warehouse {
  id: string;
  code: string;
  name: string;
  kind: string;
  is_active: boolean;
}

interface Product {
  id: string;
  code: string;
  name: string;
  product_type: string;
  unit: string;
  tracking: string;
  has_expiry: boolean;
}

const WAREHOUSE_KINDS = [
  { value: 'CENTRAL', label: 'Central' },
  { value: 'DELEGACION', label: 'Delegación' },
  { value: 'VEHICULO', label: 'Furgoneta' },
  { value: 'TALLER', label: 'Taller' },
  { value: 'CLIENTE', label: 'En cliente' },
];

const PRODUCT_TYPES = [
  { value: 'MATERIAL', label: 'Material' },
  { value: 'REPUESTO', label: 'Repuesto' },
  { value: 'EQUIPO', label: 'Equipo' },
  { value: 'CONSUMIBLE', label: 'Consumible' },
  { value: 'SERVICIO', label: 'Servicio' },
];

const TRACKING = [
  { value: 'NONE', label: 'Sin trazabilidad' },
  { value: 'LOT', label: 'Por lote' },
  { value: 'SERIAL', label: 'Por número de serie' },
];

const KIND_LABEL = Object.fromEntries(WAREHOUSE_KINDS.map((k) => [k.value, k.label]));

export function WarehouseAdminScreen(): React.ReactNode {
  const { api } = useSession();

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const [w, p] = await Promise.all([
      api.get<{ items: Warehouse[] }>('/inventory/warehouses'),
      api.get<{ items: Product[] }>('/inventory/products'),
    ]);
    setWarehouses(w.items);
    setProducts(p.items);
  }, [api]);

  useEffect(() => {
    reload()
      .catch((caught: unknown) => {
        setError(caught instanceof ApiError ? caught.message : 'No se ha podido cargar el almacén.');
      })
      .finally(() => setLoading(false));
  }, [reload]);

  // ── Almacenes ──────────────────────────────────────────────────────────
  const warehouseForm = useForm<{ id: string }>({
    mode: 'create',
    rules: { code: [required('El código')], name: [required('El nombre')] } as FieldRules,
    initial: { code: '', name: '', kind: 'CENTRAL' },
    onSubmit: (body, client) =>
      client.request<{ id: string }>('/inventory/warehouses', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSaved: () => {
      setNotice('Almacén creado.');
      void reload();
    },
  });

  // ── Artículos ──────────────────────────────────────────────────────────
  const productForm = useForm<{ id: string }>({
    mode: 'create',
    rules: { code: [required('El código')], name: [required('El nombre')] } as FieldRules,
    initial: {
      code: '',
      name: '',
      productType: 'MATERIAL',
      unit: 'ud',
      tracking: 'NONE',
      hasExpiry: false,
    },
    onSubmit: (body, client) =>
      client.request<{ id: string }>('/inventory/products', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSaved: () => {
      setNotice('Artículo creado.');
      void reload();
    },
  });

  // ── Movimientos ────────────────────────────────────────────────────────
  const receiptForm = useForm<unknown>({
    mode: 'create',
    rules: {
      productId: [required('El artículo')],
      warehouseId: [required('El almacén')],
      quantity: [required('La cantidad')],
    } as FieldRules,
    initial: { productId: '', warehouseId: '', quantity: '', unitCost: '', lotCode: '', documentRef: '' },
    onSubmit: (body, client) =>
      client.request('/inventory/receipts', { method: 'POST', body: JSON.stringify(body) }),
    onSaved: () => {
      setNotice('Entrada registrada.');
      void reload();
    },
  });

  const transferForm = useForm<unknown>({
    mode: 'create',
    rules: {
      productId: [required('El artículo')],
      fromWarehouseId: [required('El almacén de origen')],
      toWarehouseId: [required('El almacén de destino')],
      quantity: [required('La cantidad')],
    } as FieldRules,
    initial: { productId: '', fromWarehouseId: '', toWarehouseId: '', quantity: '', reason: '' },
    onSubmit: (body, client) =>
      client.request('/inventory/transfers', { method: 'POST', body: JSON.stringify(body) }),
    onSaved: () => {
      setNotice('Traspaso registrado.');
      void reload();
    },
  });

  const adjustForm = useForm<unknown>({
    mode: 'create',
    rules: {
      productId: [required('El artículo')],
      warehouseId: [required('El almacén')],
      countedQuantity: [required('La cantidad contada')],
      reason: [required('El motivo')],
    } as FieldRules,
    initial: { productId: '', warehouseId: '', countedQuantity: '', reason: '' },
    onSubmit: (body, client) =>
      client.request('/inventory/adjustments', { method: 'POST', body: JSON.stringify(body) }),
    onSaved: () => {
      setNotice('Ajuste registrado.');
      void reload();
    },
  });

  const warehouseOptions = warehouses
    .filter((w) => w.is_active)
    .map((w) => ({ value: w.id, label: `${w.name} (${KIND_LABEL[w.kind] ?? w.kind})` }));
  const productOptions = products.map((p) => ({ value: p.id, label: `${p.code} · ${p.name}` }));

  const noMasters = warehouses.length === 0 || products.length === 0;

  const tabs: Tab[] = [
    {
      id: 'almacenes',
      label: 'Almacenes',
      count: warehouses.length,
      render: () => (
        <>
          <p className="screen__note">
            Una furgoneta es un almacén que se mueve: dar de alta cada vehículo como almacén es lo
            que permite saber qué material lleva encima cada técnico.
          </p>

          {warehouses.length > 0 && (
            <div className="scroller">
              <table className="table__grid table__grid--compact">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Nombre</th>
                    <th>Tipo</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {warehouses.map((w) => (
                    <tr key={w.id}>
                      <td>{w.code}</td>
                      <td>{w.name}</td>
                      <td>{KIND_LABEL[w.kind] ?? w.kind}</td>
                      <td>
                        {w.is_active ? (
                          <span className="pill pill--ok">✓ Activo</span>
                        ) : (
                          <span className="pill pill--off">De baja</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <form className="form" onSubmit={(e) => void warehouseForm.submit(e)} noValidate>
            {warehouseForm.formError !== null && (
              <p className="screen__error" role="alert">
                {warehouseForm.formError}
              </p>
            )}
            <FormSection title="Nuevo almacén">
              <TextField
                label="Código"
                value={String(warehouseForm.values['code'] ?? '')}
                onChange={(v) => warehouseForm.set('code', v)}
                onBlur={() => warehouseForm.blur('code')}
                error={warehouseForm.errors['code']}
                required
              />
              <TextField
                label="Nombre"
                value={String(warehouseForm.values['name'] ?? '')}
                onChange={(v) => warehouseForm.set('name', v)}
                onBlur={() => warehouseForm.blur('name')}
                error={warehouseForm.errors['name']}
                required
              />
              <SelectField
                label="Tipo"
                value={String(warehouseForm.values['kind'] ?? 'CENTRAL')}
                onChange={(v) => warehouseForm.set('kind', v)}
                options={WAREHOUSE_KINDS}
                required
              />
              <button className="button button--primary" type="submit" disabled={warehouseForm.saving}>
                {warehouseForm.saving ? 'Creando…' : 'Crear almacén'}
              </button>
            </FormSection>
          </form>
        </>
      ),
    },
    {
      id: 'articulos',
      label: 'Artículos',
      count: products.length,
      render: () => (
        <>
          {products.length > 0 && (
            <div className="scroller">
              <table className="table__grid table__grid--compact">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Nombre</th>
                    <th>Tipo</th>
                    <th>Unidad</th>
                    <th>Trazabilidad</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id}>
                      <td>{p.code}</td>
                      <td>{p.name}</td>
                      <td>{PRODUCT_TYPES.find((t) => t.value === p.product_type)?.label ?? p.product_type}</td>
                      <td>{p.unit}</td>
                      <td>
                        {TRACKING.find((t) => t.value === p.tracking)?.label ?? p.tracking}
                        {p.has_expiry ? ' · con caducidad' : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <form className="form" onSubmit={(e) => void productForm.submit(e)} noValidate>
            {productForm.formError !== null && (
              <p className="screen__error" role="alert">
                {productForm.formError}
              </p>
            )}
            <FormSection title="Nuevo artículo">
              <TextField
                label="Código"
                value={String(productForm.values['code'] ?? '')}
                onChange={(v) => productForm.set('code', v)}
                onBlur={() => productForm.blur('code')}
                error={productForm.errors['code']}
                required
              />
              <TextField
                label="Nombre"
                value={String(productForm.values['name'] ?? '')}
                onChange={(v) => productForm.set('name', v)}
                onBlur={() => productForm.blur('name')}
                error={productForm.errors['name']}
                required
              />
              <SelectField
                label="Tipo"
                value={String(productForm.values['productType'] ?? 'MATERIAL')}
                onChange={(v) => productForm.set('productType', v)}
                options={PRODUCT_TYPES}
              />
              <TextField
                label="Unidad"
                value={String(productForm.values['unit'] ?? 'ud')}
                onChange={(v) => productForm.set('unit', v)}
                help="ud, kg, m, l…"
              />
              <SelectField
                label="Trazabilidad"
                value={String(productForm.values['tracking'] ?? 'NONE')}
                onChange={(v) => productForm.set('tracking', v)}
                options={TRACKING}
                help="Los productos con caducidad exigen lote: sin él no se puede retirar de circulación lo que el fabricante retire."
              />
              <CheckField
                label="Tiene caducidad"
                checked={productForm.values['hasExpiry'] === true}
                onChange={(on) => productForm.set('hasExpiry', on)}
              />
              <button className="button button--primary" type="submit" disabled={productForm.saving}>
                {productForm.saving ? 'Creando…' : 'Crear artículo'}
              </button>
            </FormSection>
          </form>
        </>
      ),
    },
    {
      id: 'movimientos',
      label: 'Movimientos',
      render: () => (
        <>
          {noMasters ? (
            <p className="screen__note screen__note--strong">
              Antes de registrar movimientos hace falta al menos un almacén y un artículo.
            </p>
          ) : (
            <>
              <form className="form" onSubmit={(e) => void receiptForm.submit(e)} noValidate>
                {receiptForm.formError !== null && (
                  <p className="screen__error" role="alert">
                    {receiptForm.formError}
                  </p>
                )}
                <FormSection title="Entrada de material">
                  <SelectField
                    label="Artículo"
                    value={String(receiptForm.values['productId'] ?? '')}
                    onChange={(v) => receiptForm.set('productId', v)}
                    onBlur={() => receiptForm.blur('productId')}
                    error={receiptForm.errors['productId']}
                    options={productOptions}
                    emptyLabel="Seleccione…"
                    required
                  />
                  <SelectField
                    label="Almacén"
                    value={String(receiptForm.values['warehouseId'] ?? '')}
                    onChange={(v) => receiptForm.set('warehouseId', v)}
                    onBlur={() => receiptForm.blur('warehouseId')}
                    error={receiptForm.errors['warehouseId']}
                    options={warehouseOptions}
                    emptyLabel="Seleccione…"
                    required
                  />
                  <TextField
                    label="Cantidad"
                    value={String(receiptForm.values['quantity'] ?? '')}
                    onChange={(v) => receiptForm.set('quantity', v)}
                    onBlur={() => receiptForm.blur('quantity')}
                    error={receiptForm.errors['quantity']}
                    required
                  />
                  <TextField
                    label="Coste unitario"
                    value={String(receiptForm.values['unitCost'] ?? '')}
                    onChange={(v) => receiptForm.set('unitCost', v)}
                    help="Alimenta el precio medio ponderado (decisión P-11). Vacío si no se conoce."
                  />
                  <TextField
                    label="Lote"
                    value={String(receiptForm.values['lotCode'] ?? '')}
                    onChange={(v) => receiptForm.set('lotCode', v)}
                    help="Obligatorio si el artículo se controla por lote."
                  />
                  <TextField
                    label="Albarán o referencia"
                    value={String(receiptForm.values['documentRef'] ?? '')}
                    onChange={(v) => receiptForm.set('documentRef', v)}
                  />
                  <button className="button button--primary" type="submit" disabled={receiptForm.saving}>
                    {receiptForm.saving ? 'Registrando…' : 'Registrar entrada'}
                  </button>
                </FormSection>
              </form>

              <form className="form" onSubmit={(e) => void transferForm.submit(e)} noValidate>
                {transferForm.formError !== null && (
                  <p className="screen__error" role="alert">
                    {transferForm.formError}
                  </p>
                )}
                <FormSection title="Traspaso entre almacenes">
                  <SelectField
                    label="Artículo"
                    value={String(transferForm.values['productId'] ?? '')}
                    onChange={(v) => transferForm.set('productId', v)}
                    onBlur={() => transferForm.blur('productId')}
                    error={transferForm.errors['productId']}
                    options={productOptions}
                    emptyLabel="Seleccione…"
                    required
                  />
                  <SelectField
                    label="Desde"
                    value={String(transferForm.values['fromWarehouseId'] ?? '')}
                    onChange={(v) => transferForm.set('fromWarehouseId', v)}
                    onBlur={() => transferForm.blur('fromWarehouseId')}
                    error={transferForm.errors['fromWarehouseId']}
                    options={warehouseOptions}
                    emptyLabel="Seleccione…"
                    required
                  />
                  <SelectField
                    label="Hasta"
                    value={String(transferForm.values['toWarehouseId'] ?? '')}
                    onChange={(v) => transferForm.set('toWarehouseId', v)}
                    onBlur={() => transferForm.blur('toWarehouseId')}
                    error={transferForm.errors['toWarehouseId']}
                    options={warehouseOptions}
                    emptyLabel="Seleccione…"
                    help="Reponer una furgoneta es un traspaso del almacén central a la del técnico."
                    required
                  />
                  <TextField
                    label="Cantidad"
                    value={String(transferForm.values['quantity'] ?? '')}
                    onChange={(v) => transferForm.set('quantity', v)}
                    onBlur={() => transferForm.blur('quantity')}
                    error={transferForm.errors['quantity']}
                    required
                  />
                  <TextField
                    label="Motivo"
                    value={String(transferForm.values['reason'] ?? '')}
                    onChange={(v) => transferForm.set('reason', v)}
                  />
                  <button className="button button--primary" type="submit" disabled={transferForm.saving}>
                    {transferForm.saving ? 'Registrando…' : 'Registrar traspaso'}
                  </button>
                </FormSection>
              </form>

              <form className="form" onSubmit={(e) => void adjustForm.submit(e)} noValidate>
                {adjustForm.formError !== null && (
                  <p className="screen__error" role="alert">
                    {adjustForm.formError}
                  </p>
                )}
                <FormSection title="Ajuste por recuento">
                  <SelectField
                    label="Artículo"
                    value={String(adjustForm.values['productId'] ?? '')}
                    onChange={(v) => adjustForm.set('productId', v)}
                    onBlur={() => adjustForm.blur('productId')}
                    error={adjustForm.errors['productId']}
                    options={productOptions}
                    emptyLabel="Seleccione…"
                    required
                  />
                  <SelectField
                    label="Almacén"
                    value={String(adjustForm.values['warehouseId'] ?? '')}
                    onChange={(v) => adjustForm.set('warehouseId', v)}
                    onBlur={() => adjustForm.blur('warehouseId')}
                    error={adjustForm.errors['warehouseId']}
                    options={warehouseOptions}
                    emptyLabel="Seleccione…"
                    required
                  />
                  <TextField
                    label="Cantidad contada"
                    value={String(adjustForm.values['countedQuantity'] ?? '')}
                    onChange={(v) => adjustForm.set('countedQuantity', v)}
                    onBlur={() => adjustForm.blur('countedQuantity')}
                    error={adjustForm.errors['countedQuantity']}
                    help="Lo que hay de verdad en la estantería. El sistema calcula la diferencia."
                    required
                  />
                  <TextField
                    label="Motivo"
                    value={String(adjustForm.values['reason'] ?? '')}
                    onChange={(v) => adjustForm.set('reason', v)}
                    onBlur={() => adjustForm.blur('reason')}
                    error={adjustForm.errors['reason']}
                    help="Obligatorio: un ajuste sin motivo es un descuadre sin explicación."
                    required
                  />
                  <button className="button button--primary" type="submit" disabled={adjustForm.saving}>
                    {adjustForm.saving ? 'Registrando…' : 'Registrar ajuste'}
                  </button>
                </FormSection>
              </form>
            </>
          )}
        </>
      ),
    },
    {
      id: 'etiquetas',
      label: 'Etiquetas',
      render: () => (
        <>
          <p className="screen__note screen__note--strong">
            Mismo papel que las etiquetas de mantenimiento: A4 de 3 × 8 a 70 × 37 mm. El QR lleva
            el código interno legible, no un token: en un estante propio no hay nada que ocultar y
            así lo entiende cualquier lector sin consultar al servidor.
          </p>
          <h3>Lotes</h3>
          <LabelPicker
            kind="lotes"
            help="La etiqueta que da trazabilidad: artículo, lote, caducidad y proveedor. Si el fabricante bloquea un lote, es lo que permite recorrer estantería y furgoneta y sacarlo. Los bloqueados y caducados salen primero."
          />
          <h3>Ubicaciones</h3>
          <LabelPicker
            kind="ubicaciones"
            help="La de inventario y picking, con el código en grande para leerlo de lejos. Las furgonetas y el taller son almacenes: también se etiquetan."
          />
          <h3>Artículos</h3>
          <LabelPicker
            kind="articulos"
            help="Identifica la caja en el estante: código, nombre y unidad. No distingue dos lotes del mismo artículo — para eso está la etiqueta de lote."
          />
        </>
      ),
    },
  ];

  return (
    <>
      {notice !== null && (
        <p className="screen__note screen__note--strong" role="status">
          {notice}
        </p>
      )}
      <DetailShell
        title="Gestión de almacén"
        subtitle={`${warehouses.length} almacenes · ${products.length} artículos`}
        tabs={tabs}
        loading={loading}
        error={error}
      />
    </>
  );
}
